// Shopping — the cart mode, built for a phone in one hand in the supermarket.
// One list in store-walk order, big tap-to-tick rows, bought items sink into
// "in the cart", and a line at the top to add things by hand (paste a whole
// list, one per line).
//
// The walk is learned, not configured: a drag (in either section) moves ONE
// item relative to its new neighbour and nothing else — the server keeps one
// store order per catalogue item AND PER STORE (the chips in the header pick
// the store; REWE and EDEKA have different aisles), so a lesson taught on
// today's list holds for every list in that store. "In the cart" lists
// things in the order they were ticked; "adopt this order" makes the active
// store's order agree with that walk.
//
// Staples (pasta, spices, tins, frozen, household) carry a "have it?" chip
// until their stock was checked: the sheet books what is left — typed, with
// an optional barcode scan that links the code to the item — and a stocked
// item drops off the list.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Check, GripVertical, Plus, ScanBarcode, Trash2, X } from "lucide-react";

import { apiFetch, fetcher, post } from "@/api/client";
import { scannerSupported, useBarcodeScanner } from "@/lib/scanner";
import { cn } from "@/lib/utils";
import { playClick, playSwitch } from "@/sounds";

export interface ShoppingItem {
  name: string;
  qty_metric: number;
  unit_metric: string | null;
  qty_display: string | null;
  recipes: string[];
  manual: boolean;
  checked: boolean;
  checked_at: string | null;
  position: number | null;
  maybe_home: boolean;
}

interface Store {
  id: number;
  name: string;
  is_active: number;
}

const SHOPPING_KEY = "/api/menu/shopping";
const STORES_KEY = "/api/menu/stores";

export default function Shopping() {
  const { data, mutate } = useSWR<{ items: ShoppingItem[]; store_id: number }>(SHOPPING_KEY, fetcher, {
    refreshInterval: 20000,
  });
  const { data: storeData, mutate: mutateStores } = useSWR<{ stores: Store[]; active: number }>(
    STORES_KEY,
    fetcher,
  );
  const [checking, setChecking] = useState<ShoppingItem | null>(null); // "have it?" sheet

  // Optimistic edits go straight into the SWR cache so a drag or tick shows
  // instantly; the server is the truth again on the revalidation that follows.
  const items = useMemo(() => data?.items ?? [], [data]);
  function show(next: ShoppingItem[]) {
    void mutate({ items: next, store_id: data?.store_id ?? 0 }, { revalidate: false });
  }
  const maybeHome = useMemo(() => items.filter((i) => !i.checked && i.maybe_home).length, [items]);

  async function pickStore(id: number) {
    if (id === storeData?.active) return;
    playClick();
    if (storeData) {
      void mutateStores({ ...storeData, active: id }, { revalidate: false });
    }
    await post(`/api/menu/stores/${id}/activate`, {});
    void mutateStores();
    void mutate();
  }

  async function addStore() {
    const name = window.prompt("New store (starts with a copy of the current walk):");
    if (!name?.trim()) return;
    playClick();
    await post("/api/menu/stores", { name: name.trim() });
    void mutateStores();
    void mutate();
  }

  const open = useMemo(() => items.filter((i) => !i.checked), [items]);
  // The cart in tick order — the walk as it actually happened.
  const bought = useMemo(
    () =>
      items
        .filter((i) => i.checked)
        .sort((a, b) => (a.checked_at ?? "").localeCompare(b.checked_at ?? "")),
    [items],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // One drag = one lesson: the dragged item goes directly after the row now
  // above it (or to the very front). Nothing else moves, on this list or any
  // other. Works the same in the open list and in the cart.
  async function dragWithin(section: ShoppingItem[], e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = section.findIndex((i) => i.name === active.id);
    const to = section.findIndex((i) => i.name === over.id);
    if (from < 0 || to < 0) return;
    const next = arrayMove(section, from, to);
    const idx = next.findIndex((i) => i.name === active.id);
    const after = idx > 0 ? next[idx - 1].name : null;
    const before = idx === 0 && next.length > 1 ? next[1].name : null;
    // optimistic: show the new order of this section
    const sectionNames = new Set(section.map((i) => i.name));
    let k = 0;
    show(items.map((i) => (sectionNames.has(i.name) ? next[k++] : i)));
    playClick();
    await post("/api/menu/shopping/order", { name: String(active.id), after, before });
    void mutate();
  }

  async function toggle(item: ShoppingItem) {
    playSwitch(!item.checked);
    show(items.map((i) => (i.name === item.name ? { ...i, checked: !i.checked } : i)));
    await post("/api/menu/shopping/check", { name: item.name, checked: !item.checked });
    void mutate();
  }

  async function remove(item: ShoppingItem) {
    playClick();
    show(items.filter((i) => i.name !== item.name));
    await apiFetch(`/api/menu/shopping/items/${encodeURIComponent(item.name)}`, {
      method: "DELETE",
    });
    void mutate();
  }

  async function add(text: string) {
    const names = text
      .split(/\r?\n|;/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (names.length === 0) return;
    playClick();
    await post("/api/menu/shopping/items", { names });
    void mutate();
  }

  async function clearBought() {
    playClick();
    await post("/api/menu/shopping/clear", {});
    void mutate();
  }

  const [adopted, setAdopted] = useState<number | null>(null);
  async function adoptOrder() {
    playClick();
    const r = await post<{ moved: number }>("/api/menu/shopping/adopt", {});
    setAdopted(r.moved);
    void mutate();
  }

  return (
    <div className="flex flex-1 flex-col overflow-y-auto">
      <div className="mx-auto flex w-full max-w-xl flex-1 flex-col gap-5 p-4 pb-8 md:p-6">
        {/* On the phone the rolled side-panel squircle sits in the top-left
            corner; the title steps right of it. */}
        <header className="flex min-h-[calc(var(--rail-width)-8px)] items-end justify-between gap-3 pl-[calc(var(--rail-width)+4px)] md:min-h-0 md:pl-0">
          <div>
            <h1 className="font-display text-2xl leading-none">shopping list</h1>
            <p className="mt-1.5 font-mono text-[11px] text-dim">
              {open.length === 0
                ? bought.length
                  ? "everything is in the cart"
                  : "nothing to buy"
                : `${open.length} to buy · planned minus stock, plus your own${
                    maybeHome ? ` · ${maybeHome} maybe at home` : ""
                  }`}
            </p>
          </div>
        </header>

        {/* store chips — the walk the list follows */}
        {storeData && (
          <div className="-mt-2 flex flex-wrap items-center gap-1.5 px-1">
            {storeData.stores.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => void pickStore(s.id)}
                className={cn(
                  "cursor-pointer rounded-full border px-3 py-1 font-mono text-[11px] transition-colors",
                  s.id === storeData.active
                    ? "border-brand-tint-border bg-brand-tint-12 text-brand-ink"
                    : "border-line-strong text-dim hover:text-foreground",
                )}
              >
                {s.name}
              </button>
            ))}
            <button
              type="button"
              onClick={() => void addStore()}
              aria-label="Add a store"
              className="flex size-6 cursor-pointer items-center justify-center rounded-full border border-line-strong text-dimmer hover:text-foreground"
            >
              <Plus className="size-3" />
            </button>
          </div>
        )}

        <AddLine onAdd={add} />

        {open.length > 0 && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(e) => void dragWithin(open, e)}
          >
            <SortableContext items={open.map((i) => i.name)} strategy={verticalListSortingStrategy}>
              <ul className="divide-y divide-line overflow-hidden rounded-[var(--radius-card)] border border-line bg-surface">
                {open.map((item) => (
                  <Row
                    key={item.name}
                    item={item}
                    onToggle={toggle}
                    onRemove={remove}
                    onCheckStock={setChecking}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        )}

        {items.length === 0 && data && (
          <p className="px-1 text-sm text-dim">
            Plan meals in Menu and whatever the kitchen lacks lands here — or add things above.
          </p>
        )}

        {bought.length > 0 && (
          <section>
            <div className="mb-2 flex items-center justify-between gap-2 px-1">
              <h2 className="font-mono text-[11px] uppercase tracking-[0.14em] text-dim">
                in the cart · {bought.length}
              </h2>
              <div className="flex gap-1.5">
                <button
                  type="button"
                  onClick={() => void adoptOrder()}
                  title="Remember the order you picked things up as the store order"
                  className="cursor-pointer rounded-full border border-brand-tint-border bg-brand-tint-08 px-3 py-1 font-mono text-[11px] text-brand-ink transition-colors hover:bg-brand-tint-12"
                >
                  {adopted === null ? "adopt this order" : adopted === 0 ? "order kept ✓" : `moved ${adopted} ✓`}
                </button>
                <button
                  type="button"
                  onClick={() => void clearBought()}
                  className="cursor-pointer rounded-full border border-line-strong px-3 py-1 font-mono text-[11px] text-dim transition-colors hover:text-foreground"
                >
                  clear bought
                </button>
              </div>
            </div>
            <p className="mb-2 px-1 font-mono text-[10px] text-dimmer">
              in the order you ticked them — drag to correct, then adopt
            </p>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={(e) => void dragWithin(bought, e)}
            >
              <SortableContext items={bought.map((i) => i.name)} strategy={verticalListSortingStrategy}>
                <ul className="divide-y divide-line overflow-hidden rounded-[var(--radius-card)] border border-line bg-surface/60">
                  {bought.map((item) => (
                    <Row key={item.name} item={item} onToggle={toggle} onRemove={remove} />
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
          </section>
        )}
      </div>

      {checking && (
        <StockSheet
          item={checking}
          onDone={() => {
            setChecking(null);
            void mutate();
          }}
        />
      )}
    </div>
  );
}

function amount(item: ShoppingItem): string | null {
  if (item.qty_metric > 0) {
    const n = Number.isInteger(item.qty_metric) ? item.qty_metric : item.qty_metric.toFixed(1);
    return `${n} ${item.unit_metric === "piece" ? "" : (item.unit_metric ?? "")}`.trim();
  }
  return item.qty_display;
}

function Row({
  item,
  onToggle,
  onRemove,
  onCheckStock,
  sortable = true,
}: {
  item: ShoppingItem;
  onToggle: (item: ShoppingItem) => void;
  onRemove: (item: ShoppingItem) => void;
  onCheckStock?: (item: ShoppingItem) => void;
  sortable?: boolean;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.name, disabled: !sortable });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const qty = amount(item);

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        "relative flex items-stretch bg-surface",
        isDragging && "z-10 shadow-[0_8px_24px_rgba(0,0,0,0.35)]",
      )}
    >
      {/* drag handle — the only thing that starts a drag, so the list still scrolls */}
      <button
        ref={setActivatorNodeRef}
        type="button"
        aria-label={`Move ${item.name}`}
        disabled={!sortable}
        {...attributes}
        {...listeners}
        className={cn(
          "flex w-10 shrink-0 cursor-grab touch-none items-center justify-center text-dimmer active:cursor-grabbing",
          !sortable && "invisible",
        )}
      >
        <GripVertical className="size-4" />
      </button>

      <button
        type="button"
        onClick={() => onToggle(item)}
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 py-3 pr-2 text-left"
      >
        <span
          className={cn(
            "flex size-6 shrink-0 items-center justify-center rounded-md border transition-colors",
            item.checked ? "border-brand bg-brand text-[var(--brand-text)]" : "border-line-strong",
          )}
        >
          {item.checked && <Check className="size-4" strokeWidth={2.5} />}
        </span>
        <span className="min-w-0 flex-1">
          <span
            className={cn(
              "block truncate text-[15px] leading-tight",
              item.checked && "text-dimmer line-through",
            )}
          >
            {item.name}
          </span>
          {item.recipes.length > 0 && (
            <span className="block truncate font-mono text-[10px] text-dimmer">
              {item.recipes.join(" · ")}
            </span>
          )}
        </span>
        {qty && (
          <span className={cn("shrink-0 font-mono text-xs text-dim", item.checked && "text-dimmer")}>
            {qty}
          </span>
        )}
      </button>

      {item.maybe_home && !item.checked && onCheckStock && (
        <button
          type="button"
          onClick={() => {
            playClick();
            onCheckStock(item);
          }}
          title="Usually in the house — check what is left before buying"
          className="my-auto mr-1 shrink-0 cursor-pointer rounded-full border border-status-warn/50 bg-[var(--fill-subtle)] px-2.5 py-1 font-mono text-[10px] text-status-warn transition-colors hover:border-status-warn"
        >
          have it?
        </button>
      )}

      {item.manual ? (
        <button
          type="button"
          onClick={() => onRemove(item)}
          aria-label={`Remove ${item.name}`}
          className="flex w-10 shrink-0 cursor-pointer items-center justify-center text-dimmer transition-colors hover:text-foreground"
        >
          <Trash2 className="size-4" />
        </button>
      ) : (
        <span className="w-3 shrink-0" />
      )}
    </li>
  );
}

function AddLine({ onAdd }: { onAdd: (text: string) => Promise<void> }) {
  const [draft, setDraft] = useState("");
  const multi = draft.includes("\n");

  async function submit() {
    const text = draft.trim();
    if (!text) return;
    setDraft("");
    await onAdd(text);
  }

  return (
    <div className="flex items-start gap-2 rounded-2xl border border-line-strong bg-surface p-1.5 pl-3">
      <Plus className="mt-2.5 size-4 shrink-0 text-dimmer" />
      <textarea
        value={draft}
        rows={multi ? Math.min(8, draft.split("\n").length) : 1}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          // Enter adds; Shift+Enter makes a new line (pasted lists keep theirs).
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            void submit();
          }
        }}
        placeholder="add, or paste a list"
        aria-label="Add to the shopping list"
        enterKeyHint="done"
        className="min-w-0 flex-1 resize-none bg-transparent py-2 font-mono text-sm text-foreground outline-none placeholder:text-dimmer"
      />
      <button
        type="button"
        onClick={() => void submit()}
        disabled={!draft.trim()}
        aria-label="Add"
        className={cn(
          "flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full bg-brand text-[var(--brand-text)]",
          "transition-[transform,opacity] duration-500 ease-epicure hover:-translate-y-0.5 active:scale-95",
          "disabled:cursor-default disabled:opacity-35 disabled:hover:translate-y-0",
        )}
      >
        <Plus className="size-4" />
      </button>
    </div>
  );
}

// ── "have it?" — book what is left of a staple ─────────────────────────────

function StockSheet({ item, onDone }: { item: ShoppingItem; onDone: () => void }) {
  const unit = item.unit_metric && item.unit_metric !== "piece" ? item.unit_metric : "×";
  const [qty, setQty] = useState("");
  const [barcode, setBarcode] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const onCode = useCallback((code: string) => {
    playSwitch(true);
    setBarcode(code);
    setScanning(false);
  }, []);
  const camera = useBarcodeScanner(videoRef, onCode, scanning);

  // Esc closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onDone();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDone]);

  async function save(quantity: number) {
    if (busy) return;
    setBusy(true);
    try {
      const r = await post<{ covered: boolean; barcode_owner: string | null }>(
        "/api/menu/shopping/stock",
        { name: item.name, quantity, barcode },
      );
      playSwitch(r.covered);
      if (r.barcode_owner) {
        setNote(`that barcode is "${r.barcode_owner}" — amount booked on ${item.name} anyway`);
        setBusy(false);
        window.setTimeout(onDone, 1800);
        return;
      }
      onDone();
    } catch {
      setNote("could not save");
      setBusy(false);
    }
  }

  const typed = Number(qty.replace(",", "."));
  const needed = item.qty_metric > 0 ? item.qty_metric : 1;

  return (
    <div
      className="fixed inset-0 z-30 flex items-end justify-center bg-black/50 md:items-center"
      onClick={onDone}
    >
      <div
        role="dialog"
        aria-label={`How much ${item.name} is left?`}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-t-[var(--radius-card)] border border-line-strong bg-popover p-5 shadow-[0_-10px_40px_rgba(0,0,0,0.35)] md:rounded-[var(--radius-card)]"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-2xl font-medium leading-tight">{item.name}</h2>
            <p className="mt-1 text-xs text-dim">
              usually in the house — how much is left?
              {item.qty_metric > 0 && (
                <span className="font-mono"> · plans need {amount(item)}</span>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onDone}
            aria-label="Close"
            className="-mr-1 -mt-1 flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-dimmer hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* scanner */}
        {scanning ? (
          <div className="relative mt-4 aspect-[4/3] overflow-hidden rounded-xl border border-line-strong bg-panel">
            <video ref={videoRef} muted playsInline className="h-full w-full object-cover" />
            {camera !== "on" && (
              <div className="absolute inset-0 flex items-center justify-center p-4 text-center text-xs text-dim">
                {camera === "starting" && "starting camera…"}
                {camera === "denied" && "Camera blocked — allow it in the browser settings."}
                {camera === "unsupported" && "No barcode scanner in this browser — type the amount below."}
              </div>
            )}
            <button
              type="button"
              onClick={() => setScanning(false)}
              className="absolute right-2 top-2 cursor-pointer rounded-full bg-black/60 px-3 py-1 font-mono text-[11px] text-[#f5f2e8]"
            >
              stop
            </button>
          </div>
        ) : (
          <div className="mt-4 flex items-center gap-2">
            {scannerSupported() ? (
              <button
                type="button"
                onClick={() => {
                  playClick();
                  setScanning(true);
                }}
                className="flex cursor-pointer items-center gap-2 rounded-full border border-line-strong px-3.5 py-2 text-xs font-semibold text-dim hover:text-foreground"
              >
                <ScanBarcode className="size-4" />
                {barcode ? "scan again" : "scan the pack"}
              </button>
            ) : (
              <span className="text-[11px] text-dimmer">no scanner in this browser — type the amount</span>
            )}
            {barcode && (
              <span className="truncate font-mono text-[11px] text-brand-ink">code {barcode} ✓</span>
            )}
          </div>
        )}

        {/* amount */}
        <div className="mt-4 flex items-center gap-2">
          <input
            autoFocus={!scanning}
            type="text"
            inputMode="decimal"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && Number.isFinite(typed) && qty.trim()) void save(typed);
            }}
            placeholder="amount left"
            aria-label="Amount left"
            className="min-w-0 flex-1 rounded-lg border border-line-strong bg-transparent px-3 py-2.5 font-mono text-sm outline-none focus:border-brand-ink"
          />
          <span className="w-8 shrink-0 font-mono text-xs text-dim">{unit}</span>
          <button
            type="button"
            disabled={busy || !qty.trim() || !Number.isFinite(typed) || typed < 0}
            onClick={() => void save(typed)}
            className="cursor-pointer rounded-full bg-brand px-5 py-2.5 text-xs font-bold text-primary-foreground disabled:opacity-35"
          >
            save
          </button>
        </div>

        <div className="mt-3 flex gap-1.5">
          <button
            type="button"
            disabled={busy}
            onClick={() => void save(0)}
            className="flex-1 cursor-pointer rounded-full border border-line-strong py-2 text-xs font-semibold text-dim hover:text-foreground disabled:opacity-35"
          >
            none left — buy it
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void save(needed)}
            title={`books ${needed} ${unit} — enough for the plan`}
            className="flex-1 cursor-pointer rounded-full border border-brand-tint-border bg-brand-tint-08 py-2 text-xs font-semibold text-brand-ink hover:bg-brand-tint-12 disabled:opacity-35"
          >
            enough at home
          </button>
        </div>

        {note && <p className="mt-3 font-mono text-[11px] text-status-warn">{note}</p>}
        <p className="mt-3 text-[10px] text-dimmer">
          A scanned code is remembered on the item, so the fridge tablet knows the pack from then on.
        </p>
      </div>
    </div>
  );
}
